import {property} from "lit/decorators/property.js";
import {container} from "tsyringe";
import {Entity} from "../../domain/entities/entity";
import {EntityManager} from "../../persistence/entity.manager";
import {ClassOf} from "../../types/class-of.type";
import {AbstractDialogComponent} from "./abstract-dialog.component";
import {AuthorizationService} from "../../services/authorization.service";
import {ExecutionVerification} from "../../services/execution-verification.type";
import {CommandName} from "../../commands/command-name.enum";

export abstract class AbstractEntitiesDialogComponent<T extends Entity> extends AbstractDialogComponent {

    // @deprecated
    @property({type: Boolean})
    public creationMode: ClassOf<T> | null = null;

    @property({type: Object})
    public selectedEntity!: T | null | undefined;

    @property({type: Object})
    public template: T | null = null;

    protected _isReadOnly = new Map<T, boolean>();

    private _authorizationService = container.resolve(AuthorizationService);

    private _lastEntityChangeTick = -1;

    private _withoutLastChangeValidationFlag: boolean = false;

    protected _entities: T[] = [];

    @property({type: Object})
    public get entities(): T[] {
        return this._entities;
    }

    public set entities(entities: T[]) {
        const lastChangeTick = Math.max(...entities.map(e => e.lastChangeTick));

        if (entities.length === 0 || (!this._withoutLastChangeValidationFlag && this._lastEntityChangeTick === lastChangeTick)) {
            return;
        }

        this._lastEntityChangeTick = lastChangeTick;
        const oldEntities = this._entities;
        this._entities = entities;

        this._isSomeReadOnly(entities).then(executionVerifications => {
            for (const entity of entities) {
                this._isReadOnly.set(entity, !executionVerifications.filter(v => v.entity === entity).some(v => v.canExecute));
            }
            this.requestUpdate(`entities`, oldEntities);
        });

    }

    public async createNewEntity(): Promise<void> {
        const newIssue = this.template
            ? await this.template.clone()
            : await container.resolve(EntityManager).getRepositoryForEntity<T>(this.selectedEntity!.constructor as ClassOf<T>)!.create();

        if (!newIssue) {
            console.error(`Konnte keine neue Ressource vom Typ ${this.selectedEntity!.constructor.name} anlegen`)
        }

        this.entities = [newIssue, ...this.entities];
        this.selectedEntity = newIssue;
    }

    protected _withoutLastChangeValidation = (cb: () => void): void => {
        this._withoutLastChangeValidationFlag = true;
        cb();
        this._withoutLastChangeValidationFlag = false;
    }

    private async _isSomeReadOnly(entities: T[]): Promise<ExecutionVerification[]> {
        return this._authorizationService.canExecute(entities.flatMap(entity => [<ExecutionVerification>{
            entity,
            commandName: entity.isNew() ? CommandName.CREATE_ISSUE : CommandName.UPDATE_ISSUE,
            canExecute: false
        }, <ExecutionVerification>{
            entity,
            commandName: CommandName.DELETE_ISSUE,
            canExecute: false
        }]));
    }
}